import { formatApiError } from '../../utils/formatApiError'

const TOKEN_KEY = 'aonlastay_admin_token'
const ADMIN_KEY = 'aonlastay_admin_profile'

export type AdminProfile = {
  id: number
  username: string
  email?: string
  name?: string
}

export function getAdminToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY)
}

export function getAdminProfile(): AdminProfile | null {
  const raw = sessionStorage.getItem(ADMIN_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AdminProfile
  } catch {
    return null
  }
}

export function setAdminSession(token: string, admin: AdminProfile) {
  sessionStorage.setItem(TOKEN_KEY, token)
  sessionStorage.setItem(ADMIN_KEY, JSON.stringify(admin))
}

export function clearAdminSession() {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(ADMIN_KEY)
}

function apiBase(): string {
  const envUrl = import.meta.env.VITE_API_BASE_URL?.trim()
  if (envUrl) return envUrl.replace(/\/$/, '')
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8002/api/v1'
  }
  return 'https://silverfoil.napps.in/aonlastay-api/api/v1'
}

function authHeaders(): HeadersInit {
  const token = getAdminToken()
  const h: Record<string, string> = { Accept: 'application/json' }
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

function abortTimeout(ms: number) {
  return AbortSignal.timeout(ms)
}

async function adminFetch(url: string, init: RequestInit = {}, timeoutMs = 12000) {
  const res = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
    signal: init.signal ?? abortTimeout(timeoutMs),
  })
  return parseJson(res)
}

async function parseJson(res: Response) {
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const detail = json.detail
    const msg =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg).join(', ')
          : json.message || res.statusText
    throw new Error(formatApiError(msg || 'Request failed'))
  }
  return json
}

export async function adminLogin(username: string, password: string) {
  let res: Response
  try {
    res = await fetch(`${apiBase()}/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ username, password }),
    })
  } catch (err) {
    throw new Error(formatApiError(
      err instanceof Error
        ? err.message
        : 'Cannot reach API. Check network or try again in a minute.'
    ))
  }
  const json = await parseJson(res)
  setAdminSession(json.token, json.admin)
  return json.admin as AdminProfile
}

export async function adminMe() {
  const res = await fetch(`${apiBase()}/admin/auth/me`, { headers: authHeaders() })
  return parseJson(res)
}

export async function fetchBatches(limit = 25) {
  const res = await fetch(`${apiBase()}/admin/listings/batches?limit=${limit}`, {
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function uploadCsv(file: File) {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${apiBase()}/admin/listings/csv/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
  })
  return parseJson(res)
}

export async function clearListings() {
  const res = await fetch(`${apiBase()}/admin/listings/clear`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function runImport(
  batchUuid: string,
  opts: { chunk_size: number; retry_failed_first?: boolean; clear_before?: boolean }
) {
  const res = await fetch(`${apiBase()}/admin/listings/import/${batchUuid}/run`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return parseJson(res)
}

export async function stopImport(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/listings/import/${batchUuid}/stop`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function fetchBatchStatus(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/listings/import/${batchUuid}/status`, {
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function fetchLogs(batchUuid: string, limit = 200) {
  const res = await fetch(`${apiBase()}/admin/listings/import/${batchUuid}/logs?limit=${limit}`, {
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function fetchVendorNotifications(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/listings/import/${batchUuid}/vendor-notifications`, {
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function fetchVendorStatus(vendorId?: number, username?: string) {
  const q = vendorId ? `vendor_id=${vendorId}` : `username=${encodeURIComponent(username || '')}`
  const res = await fetch(`${apiBase()}/vendor/first-login-status?${q}`, {
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function fetchHealth() {
  const base = apiBase().replace(/\/api\/v1$/, '')
  const res = await fetch(`${base}/health`, { signal: abortTimeout(8000) })
  return res.json()
}

// ─── BNB Enrichment ───

export async function fetchBnbBatches(limit = 25) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/batches?limit=${limit}`, { headers: authHeaders() })
  return parseJson(res)
}

export async function uploadBnbRawCsv(file: File) {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
  })
  return parseJson(res)
}

export async function fetchBnbStatus(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/${batchUuid}/status`, { headers: authHeaders() })
  return parseJson(res)
}

export async function fetchBnbRows(batchUuid: string, limit = 200) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/${batchUuid}/rows?limit=${limit}`, { headers: authHeaders() })
  return parseJson(res)
}

export async function fetchBnbLogs(batchUuid: string, limit = 200) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/${batchUuid}/logs?limit=${limit}`, { headers: authHeaders() })
  return parseJson(res)
}

export async function runBnbEnrichment(
  batchUuid: string,
  opts: { chunk_size: number; ai_delay_seconds: number; retry_failed?: boolean }
) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/${batchUuid}/run`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  })
  return parseJson(res)
}

export async function stopBnbEnrichment(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/${batchUuid}/stop`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return parseJson(res)
}

export async function promoteBnbToImport(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/bnb-enrichment/${batchUuid}/promote-to-import`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return parseJson(res)
}

export function downloadEnrichedCsv(batchUuid: string) {
  const token = getAdminToken()
  const url = `${apiBase()}/admin/bnb-enrichment/${batchUuid}/download`
  const a = document.createElement('a')
  a.href = url
  a.download = `enriched-${batchUuid}.csv`
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => r.blob())
    .then((blob) => {
      a.href = URL.createObjectURL(blob)
      a.click()
      URL.revokeObjectURL(a.href)
    })
    .catch(() => window.open(url, '_blank'))
}

// ─── Airbnb Scraper ───

// ─── Airbnb City Scraper ───

export async function fetchScrapeCities(limit = 50) {
  return adminFetch(`${apiBase()}/admin/airbnb-scraper/cities?limit=${limit}`)
}

export async function fetchScrapeCity(citySlug: string) {
  return adminFetch(`${apiBase()}/admin/airbnb-scraper/cities/${encodeURIComponent(citySlug)}`)
}

export async function fetchCityListings(citySlug: string, limit = 500, offset = 0) {
  return adminFetch(
    `${apiBase()}/admin/airbnb-scraper/cities/${encodeURIComponent(citySlug)}/listings?limit=${limit}&offset=${offset}`,
  )
}

export async function fetchCityListingDetail(citySlug: string, listingId: string) {
  const res = await fetch(
    `${apiBase()}/admin/airbnb-scraper/cities/${encodeURIComponent(citySlug)}/listings/${encodeURIComponent(listingId)}`,
    { headers: authHeaders() },
  )
  return parseJson(res)
}

export async function enrichCityListings(citySlug: string, listingIds: string[], force = true) {
  const res = await fetch(`${apiBase()}/admin/airbnb-scraper/cities/${encodeURIComponent(citySlug)}/enrich`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ listing_ids: listingIds, force }),
  })
  return parseJson(res)
}

export function downloadCityCsv(citySlug: string) {
  const token = getAdminToken()
  const url = `${apiBase()}/admin/airbnb-scraper/cities/${encodeURIComponent(citySlug)}/download`
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${citySlug}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    })
}

export async function fetchAirbnbBatches(limit = 25) {
  const res = await fetch(`${apiBase()}/admin/airbnb-scraper/batches?limit=${limit}`, { headers: authHeaders() })
  return parseJson(res)
}

export async function startAirbnbScrape(body: {
  search_url: string
  max_listings: number
  place_label: string
  months_ahead?: number
}) {
  const res = await fetch(`${apiBase()}/admin/airbnb-scraper/start`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, headless: true }),
  })
  return parseJson(res)
}

export async function fetchAirbnbStatus(batchUuid: string) {
  return adminFetch(`${apiBase()}/admin/airbnb-scraper/${batchUuid}/status`)
}

export async function fetchAirbnbListings(batchUuid: string, limit = 50) {
  return adminFetch(`${apiBase()}/admin/airbnb-scraper/${batchUuid}/listings?limit=${limit}`)
}

export async function fetchAirbnbLogs(batchUuid: string, limit = 200) {
  return adminFetch(`${apiBase()}/admin/airbnb-scraper/${batchUuid}/logs?limit=${limit}`)
}

export async function stopAirbnbScrape(batchUuid: string) {
  const res = await fetch(`${apiBase()}/admin/airbnb-scraper/${batchUuid}/stop`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return parseJson(res)
}

export function downloadAirbnbCsv(batchUuid: string) {
  const token = getAdminToken()
  const url = `${apiBase()}/admin/airbnb-scraper/${batchUuid}/download`
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `airbnb-${batchUuid.slice(0, 8)}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    })
}
